import { useCallback, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CalendarDays, Check } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { DEFAULT_DATE_FORMAT, Module } from "@/constants";
import {
  formatDate,
  formatDateTime,
  formatMonthYear,
} from "@/lib/utils/formatters";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectPeriodConfirmationStatus } from "@/modules/projects/hooks/useProjectPeriodConfirmationStatus";
import { useCreateProjectPeriodConfirmation } from "@/modules/projects/hooks/useCreateProjectPeriodConfirmation";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { getNextPeriod } from "@/modules/projects/lib/getNextPeriod";
import { periodToDate } from "@/modules/projects/lib/periodToDate";
import {
  CriterionRow,
  VARIANT_REQUIRED,
  VARIANT_SUGGESTED,
} from "./CriterionRow";

const ProjectPeriodConfirmation = () => {
  const { t, i18n } = useTranslation();
  const projectId = useProjectId();
  const [period] = useState(() => getNextPeriod());
  const projectQuery = useProjectQuery(projectId);
  const statusQuery = useProjectPeriodConfirmationStatus(projectId, period);
  const createMutation = useCreateProjectPeriodConfirmation();
  const canConfirm = useModulePermissions(
    Module.ProjectPeriodConfirmations,
  ).project_period_confirmations_confirm;

  const additionalBreadcrumb = useMemo(
    () => [
      {
        label: t("projectPeriodConfirmation.breadcrumb"),
      },
    ],
    [t],
  );
  const breadcrumbItems = useProjectBreadcrumb(
    projectQuery,
    additionalBreadcrumb,
  );

  const status = statusQuery.data;
  const isLoading = statusQuery.isLoading || projectQuery.isLoading;

  const formattedPeriod = formatMonthYear(periodToDate(period), i18n.language);

  const handleConfirm = useCallback(() => {
    if (!projectId) {
      return;
    }
    createMutation.mutate({ project_id: projectId, period });
  }, [createMutation, period, projectId]);

  const requiredCriteria = useMemo(() => {
    if (!status) {
      return [];
    }
    return (status.required_criteria ?? []).filter(
      ({ is_applicable }) => is_applicable,
    );
  }, [status]);

  const suggestedCriteria = useMemo(() => {
    if (!status) {
      return [];
    }
    return (status.suggested_criteria ?? []).filter(
      ({ is_applicable }) => is_applicable !== false,
    );
  }, [status]);

  return (
    <>
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        title={t("projectPeriodConfirmation.title")}
      />
      <div className="space-y-4">
        <ProjectDetailsAndNavigation />

        {isLoading && <Spinner className="h-32 w-full" />}

        {!isLoading && status && (
          <div className="space-y-4 rounded-md border bg-background p-4">
            <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:flex-wrap">
              <h2 className="text-lg font-semibold">
                {t("projectPeriodConfirmation.heading", {
                  period: formattedPeriod,
                })}
              </h2>
              <div className="flex items-center gap-2 text-sm">
                <span>{t("projectPeriodConfirmation.windowLabel")}:</span>
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-4 w-4" />
                  <span className="font-semibold">
                    {formatDate(
                      status.confirmation_window_start_date,
                      DEFAULT_DATE_FORMAT,
                    )}
                  </span>
                  <span className="text-muted-foreground">–</span>
                  <span className="font-semibold">
                    {formatDate(
                      status.confirmation_window_end_date,
                      DEFAULT_DATE_FORMAT,
                    )}
                  </span>
                </span>

                {!status.is_confirmation_window_open && (
                  <Badge variant="secondary">
                    {t("projectPeriodConfirmation.windowClosedBadge")}
                  </Badge>
                )}
              </div>
            </header>

            {status.is_confirmed && (
              <Alert variant="success">
                <AlertTitle>
                  {t("projectPeriodConfirmation.alreadyConfirmedTitle")}
                </AlertTitle>
                <AlertDescription>
                  <Trans
                    i18nKey="projectPeriodConfirmation.alreadyConfirmed"
                    values={{
                      period: formatMonthYear(
                        periodToDate(status.period),
                        i18n.language,
                      ),
                      user: status.confirmation_user_name,
                      role: status.confirmation_user_role_name,
                      date: formatDateTime(status.confirmed_at),
                    }}
                    components={[
                      <strong key="period" />,
                      <strong key="user" />,
                      <strong key="date" />,
                    ]}
                  />
                </AlertDescription>
              </Alert>
            )}

            {!status.is_confirmed && !status.is_confirmation_window_open && (
              <Alert variant="warning">
                <AlertDescription>
                  {t("projectPeriodConfirmation.outsideWindow")}
                </AlertDescription>
              </Alert>
            )}

            {requiredCriteria.length && (
              <section>
                <h3 className="font-medium mb-1">
                  {t("projectPeriodConfirmation.requiredCriteria")}
                </h3>
                <ul className="py-3 flex flex-col gap-2">
                  {requiredCriteria.map(({ code, is_met, details }) => (
                    <CriterionRow
                      key={code}
                      code={code}
                      isMet={is_met}
                      projectId={projectId}
                      details={details}
                      variant={VARIANT_REQUIRED}
                    />
                  ))}
                </ul>
              </section>
            )}

            {suggestedCriteria.length > 0 && (
              <section>
                <h3 className="font-medium mb-1">
                  {t("projectPeriodConfirmation.suggestedCriteria")}
                </h3>
                <ul className="py-3 flex flex-col gap-3">
                  {suggestedCriteria.map(({ code, is_met, details }) => (
                    <CriterionRow
                      key={code}
                      code={code}
                      isMet={is_met}
                      details={details}
                      variant={VARIANT_SUGGESTED}
                    />
                  ))}
                </ul>
              </section>
            )}

            {!status.is_confirmed && canConfirm && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleConfirm}
                  disabled={!status.can_confirm || createMutation.isPending}
                >
                  <Check className="h-4 w-4" />
                  {t("projectPeriodConfirmation.confirmButton")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectPeriodConfirmation;
